/**
 * Ambient animation ของแต่ละฉาก — ภาพฉากเป็น PNG นิ่ง เจ้าของงานขอให้ "ขยับ"
 *
 * ทุกอย่างเป็น texture ที่วาดเองตอนรัน (`generateTexture`) ไม่มีไฟล์ asset เพิ่ม
 * และอยู่ระหว่าง bg (depth 0) กับตัวละคร (depth 10) — จอ present ใช้โค้ดเดียวกัน
 * โดยอัตโนมัติเพราะเป็นส่วนหนึ่งของ PlayerScene (กฎเหล็กข้อ 4)
 *
 * จำนวน object จงใจน้อย (≤ ~30 ชิ้น/ฉาก) — จอจริงต้องคง 60 fps และหน้า present
 * รัน 6 ฉากพร้อมกันบนเครื่องเดียว
 */
import type Phaser from "phaser";

import { SCENE_H, SCENE_W, type SceneId } from "@py/protocol";

type S = Phaser.Scene;

/** สุ่มแบบกำหนด seed ได้ — ฉากเดียวกันบนจอจริงกับ present ขยับเหมือนกัน */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function softCircle(sc: S, key: string, r: number, color: number, alpha = 1): void {
  if (sc.textures.exists(key)) return;
  const g = sc.add.graphics();
  // วาดวงซ้อนไล่ alpha แทน gradient — Graphics ไม่มี radial gradient ให้ใช้
  for (let i = 4; i >= 1; i -= 1) {
    g.fillStyle(color, (alpha * i) / 4);
    g.fillCircle(r, r, (r * (5 - i)) / 4);
  }
  g.generateTexture(key, r * 2, r * 2);
  g.destroy();
}

function cloudTexture(sc: S, key: string): void {
  if (sc.textures.exists(key)) return;
  const g = sc.add.graphics();
  g.fillStyle(0xffffff, 1);
  // เมฆ 8-bit ก้อนเหลี่ยมๆ ให้เข้ากับฉาก pixel
  g.fillRect(20, 24, 100, 28);
  g.fillRect(36, 8, 44, 20);
  g.fillRect(70, 14, 36, 14);
  g.generateTexture(key, 140, 56);
  g.destroy();
}

function petalTexture(sc: S, key: string): void {
  if (sc.textures.exists(key)) return;
  const g = sc.add.graphics();
  g.fillStyle(0xffd2e2, 1);
  g.fillEllipse(9, 6, 16, 9);
  g.fillStyle(0xffb7d0, 0.9);
  g.fillEllipse(11, 7, 9, 5);
  g.generateTexture(key, 18, 12);
  g.destroy();
}

/** ฉาก 1 SKY BRICKS — เมฆลอยช้าๆ วนกลับมาฝั่งซ้าย */
function skyClouds(sc: S): void {
  cloudTexture(sc, "amb-cloud");
  const r = rng(11);
  for (let i = 0; i < n(4); i += 1) {
    const y = 140 + r() * 620;
    const scale = 1.2 + r() * 1.6;
    const img = sc.add.image(r() * SCENE_W, y, "amb-cloud")
      .setDepth(2).setAlpha(0.85).setScale(scale);
    sc.tweens.add({
      targets: img,
      x: SCENE_W + 120 * scale,
      duration: (40_000 + r() * 40_000) * (SCENE_W - img.x) / SCENE_W,
      onComplete: function repeat(this: Phaser.Tweens.Tween) {
        img.x = -120 * scale;
        sc.tweens.add({
          targets: img, x: SCENE_W + 120 * scale,
          duration: 40_000 + r() * 40_000, onComplete: repeat,
        });
      },
    });
  }
}

/** ฉาก 2 FRUIT MAZE — power pellet 4 มุมเต้นตุบๆ แบบแพ็กแมน */
function mazePulse(sc: S): void {
  softCircle(sc, "amb-pellet", 26, 0xd8ff5a, 0.9);
  for (const [x, y] of [[90, 90], [SCENE_W - 90, 90], [90, SCENE_H - 150], [SCENE_W - 90, SCENE_H - 150]]) {
    const img = sc.add.image(x!, y!, "amb-pellet").setDepth(2).setAlpha(0.75);
    sc.tweens.add({
      targets: img, scale: { from: 0.7, to: 1.25 }, alpha: { from: 0.35, to: 0.85 },
      duration: 650, yoyo: true, repeat: -1, ease: "sine.inout",
    });
  }
}

/** ฉาก 3 NEON BUS STOP — ป้ายนีออนกะพริบ + หน้าต่างตึกติดๆ ดับๆ */
function neonFlicker(sc: S): void {
  const r = rng(33);
  // ตำแหน่งอิงภาพ tier4: ป้ายนีออนแนวตั้งซ้าย-ขวา และแถบหน้าต่างช่วงกลางภาพ
  for (const [x, y, w, h, color] of [
    [86, 690, 40, 210, 0xff6cbe], [1002, 620, 40, 260, 0x6cf0ff],
  ] as const) {
    const g = sc.add.rectangle(x, y, w, h, color, 0.16).setDepth(2).setBlendMode("ADD");
    sc.time.addEvent({
      delay: 90, loop: true,
      callback: () => {
        // นีออนจริงกะพริบไม่เป็นจังหวะ — สุ่มหนักๆ นานๆ ทีค่อยวูบ
        g.setAlpha(r() < 0.06 ? 0.02 : 0.10 + r() * 0.14);
      },
    });
  }
  softCircle(sc, "amb-window", 10, 0xffd27a, 0.9);
  for (let i = 0; i < n(14); i += 1) {
    const img = sc.add.image(120 + r() * 840, 300 + r() * 700, "amb-window")
      .setDepth(2).setAlpha(0).setBlendMode("ADD");
    sc.time.addEvent({
      delay: 2000 + r() * 6000, loop: true,
      callback: () => img.setAlpha(img.alpha > 0 ? 0 : 0.5 + r() * 0.4),
    });
  }
}

/** ฉาก 4 SPIRIT FOREST — ดวงวิญญาณ/สปอร์ลอยขึ้นช้าๆ */
function forestOrbs(sc: S): void {
  softCircle(sc, "amb-orb", 16, 0xcfefff, 0.8);
  const r = rng(55);
  for (let i = 0; i < n(12); i += 1) {
    const x = 60 + r() * (SCENE_W - 120);
    const img = sc.add.image(x, SCENE_H * (0.3 + r() * 0.7), "amb-orb")
      .setDepth(2).setAlpha(0.25 + r() * 0.4).setScale(0.5 + r() * 1.1)
      .setBlendMode("ADD");
    sc.tweens.add({
      targets: img,
      y: -40,
      x: x + (r() - 0.5) * 220,
      duration: 16_000 + r() * 20_000,
      repeat: -1,
      onRepeat: () => { img.y = SCENE_H + 30; img.x = 60 + r() * (SCENE_W - 120); },
    });
    sc.tweens.add({
      targets: img, alpha: { from: 0.15, to: 0.6 },
      duration: 1800 + r() * 1500, yoyo: true, repeat: -1, ease: "sine.inout",
    });
  }
}

/** ฉาก 5 SAKURA VILLAGE — กลีบซากุระปลิวทแยงพร้อมส่ายซ้ายขวา */
function sakuraPetals(sc: S): void {
  petalTexture(sc, "amb-petal");
  const r = rng(77);
  for (let i = 0; i < n(24); i += 1) {
    const img = sc.add.image(r() * SCENE_W, r() * SCENE_H, "amb-petal")
      .setDepth(i % 2 === 0 ? 2 : 11) // ครึ่งหนึ่งปลิวหน้าตัวละคร — ให้ฉากมีมิติ
      .setAlpha(0.6 + r() * 0.4)
      .setScale(0.8 + r() * 1.4)
      .setAngle(r() * 360);
    const fall = () => {
      const dur = 9000 + r() * 9000;
      img.y = -20;
      img.x = r() * SCENE_W;
      sc.tweens.add({ targets: img, y: SCENE_H + 20, duration: dur, onComplete: fall });
      sc.tweens.add({
        targets: img, x: img.x + 130 + r() * 160, angle: img.angle + 200 + r() * 260,
        duration: dur, ease: "sine.inout",
      });
    };
    // เริ่มจากตำแหน่งสุ่มกลางจอก่อน แล้วค่อยเข้าวงจรตกจากขอบบน
    sc.tweens.add({
      targets: img, y: SCENE_H + 20, x: img.x + 100 + r() * 140,
      angle: img.angle + 220, duration: (1 - img.y / SCENE_H) * 12_000 + 800,
      onComplete: fall,
    });
  }
}

/** จุดเข้าเดียวจาก PlayerScene.create() */
/** จำนวนชิ้นส่วนคูณตามโหมด — present รัน 6 ฉากพร้อมกัน ต้องเบากว่าจอจริง */
let AMBIENT_SCALE = 1;

export function mountAmbient(sc: S, scene: SceneId, present = false): void {
  AMBIENT_SCALE = present ? 0.5 : 1;
  mount(sc, scene);
}

function n(base: number): number {
  return Math.max(2, Math.round(base * AMBIENT_SCALE));
}

function mount(sc: S, scene: SceneId): void {
  switch (scene) {
    case 1: skyClouds(sc); break;
    case 2: mazePulse(sc); break;
    case 3: neonFlicker(sc); break;
    case 4: forestOrbs(sc); break;
    case 5: sakuraPetals(sc); break;
    default: break; // ฉาก 0 มี UI ของตัวเอง (DOM) — ไม่เพิ่มอะไร
  }
}
